/**
 * <phototron-material> Web Component
 * 
 * Displays 2D material views with dynamic mouse-controlled lighting.
 * Uses PBR Specular Workflow (Albedo, Normal, Specular maps).
 * 
 * @example
 * ```html
 * <phototron-material
 *   asset-id="shoe-001"
 *   api-key="pk_live_xxxxx"
 *   width="400"
 *   height="400"
 *   light-intensity="0.8"
 *   interactive="true">
 * </phototron-material>
 * ```
 * 
 * @example Direct texture URLs (without API)
 * ```html
 * <phototron-material
 *   albedo-url="https://example.com/albedo.webp"
 *   normal-url="https://example.com/normal.webp"
 *   specular-url="https://example.com/specular.webp"
 *   width="400"
 *   height="400">
 * </phototron-material>
 * ```
 */

import { WebGLRenderer } from '../core/webgl-renderer';
import { ApiClient } from '../core/api-client';
import { TextureUrls, LightingConfig, DEFAULT_LIGHTING, AssetData, AssetLighting } from '../core/types';

const TEMPLATE = document.createElement('template');
TEMPLATE.innerHTML = `
  <style>
    :host {
      display: block;
      position: relative;
      overflow: hidden;
    }
    
    .container {
      width: 100%;
      height: 100%;
      position: relative;
    }
    
    canvas {
      width: 100%;
      height: 100%;
      display: block;
    }
    
    .loading {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--phototron-bg, #050505);
      color: var(--phototron-text, #fff);
      font-family: system-ui, sans-serif;
      font-size: 14px;
      transition: opacity 0.3s ease;
    }
    
    .loading.hidden {
      opacity: 0;
      pointer-events: none;
    }
    
    .loading-spinner {
      width: 24px;
      height: 24px;
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-top-color: var(--phototron-accent, #00e5ff);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    .error {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--phototron-bg, #050505);
      color: #ff6b6b;
      font-family: system-ui, sans-serif;
      font-size: 14px;
      padding: 20px;
      text-align: center;
      box-sizing: border-box;
    }
    
    .error.hidden {
      display: none;
    }
  </style>
  
  <div class="container">
    <canvas></canvas>
    <div class="loading">
      <div class="loading-spinner"></div>
    </div>
    <div class="error hidden"></div>
  </div>
`;

export class PhototronMaterial extends HTMLElement {
  private renderer: WebGLRenderer | null = null;
  // Display tuning from the manifest (attributes take precedence).
  private manifestLighting: AssetLighting | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private loadingEl: HTMLElement | null = null;
  private errorEl: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private _isComponentConnected = false;

  static get observedAttributes(): string[] {
    return [
      'asset-id',
      'api-key',
      'api-url',
      'manifest-url',
      'albedo-url',
      'normal-url',
      'specular-url',
      'roughness-url',
      'metalness-url',
      'albedo-preview-url',
      'normal-preview-url',
      'specular-preview-url',
      'roughness-preview-url',
      'metalness-preview-url',
      'width',
      'height',
      'light-intensity',
      'ambient-intensity',
      'specular-intensity',
      'specular-multiplier',
      'interactive',
      'progressive-loading',
      'material-scale',
      'vertical-offset',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this._isComponentConnected = true;
    this.shadowRoot!.appendChild(TEMPLATE.content.cloneNode(true));

    this.canvas = this.shadowRoot!.querySelector('canvas');
    this.loadingEl = this.shadowRoot!.querySelector('.loading');
    this.errorEl = this.shadowRoot!.querySelector('.error');

    // Set dimensions
    this.updateDimensions();

    // Setup resize observer
    this.resizeObserver = new ResizeObserver(() => {
      if (this.renderer) {
        // Canvas resize is handled by the renderer
      }
    });
    this.resizeObserver.observe(this);

    // Initialize
    this.initialize();
  }

  disconnectedCallback(): void {
    this._isComponentConnected = false;
    this.cleanup();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    if (name === 'width' || name === 'height') {
      this.updateDimensions();
    }

    // Reinitialize if key attributes change
    if (['asset-id', 'api-key', 'manifest-url', 'albedo-url', 'normal-url', 'specular-url'].includes(name)) {
      if (this._isComponentConnected) {
        this.initialize();
      }
    }

    // Update lighting on the fly
    if (this.renderer) {
      if (['light-intensity', 'ambient-intensity', 'specular-intensity', 'specular-multiplier'].includes(name)) {
        this.updateLighting();
      }
      if (name === 'material-scale') {
        this.renderer.setMaterialScale(parseFloat(newValue) || 1.0);
      }
      if (name === 'vertical-offset') {
        this.renderer.setVerticalOffset(parseFloat(newValue) || 0.0);
      }
    }
  }

  private updateDimensions(): void {
    const width = this.getAttribute('width');
    const height = this.getAttribute('height');

    if (width) {
      this.style.width = width.includes('%') || width.includes('px') ? width : `${width}px`;
    }
    if (height) {
      this.style.height = height.includes('%') || height.includes('px') ? height : `${height}px`;
    }
  }

  // Effective lighting from attribute > manifest lighting > default. The
  // relative factors (light-intensity, specular-intensity) scale
  // DEFAULT_LIGHTING; ambient-intensity and specular-multiplier stay absolute
  // overrides for API compatibility.
  private computeLighting(): Partial<LightingConfig> {
    const attr = (name: string): number => {
      const v = this.getAttribute(name);
      return v ? parseFloat(v) : NaN;
    };
    const config: Partial<LightingConfig> = {};

    // Overall albedo brightness: scales diffuse AND ambient together so the
    // diffuse/ambient balance is preserved ("exposure" behaviour).
    let light = attr('light-intensity');
    if (isNaN(light) && this.manifestLighting?.lightIntensity != null) {
      light = this.manifestLighting.lightIntensity;
    }
    if (!isNaN(light)) {
      config.diffuseIntensity = DEFAULT_LIGHTING.diffuseIntensity * light;
      config.ambientIntensity = DEFAULT_LIGHTING.ambientIntensity * light;
    }

    const ambient = attr('ambient-intensity');
    if (!isNaN(ambient)) {
      config.ambientIntensity = ambient;
    }

    // Specular highlights, decoupled from the albedo brightness.
    let spec = attr('specular-intensity');
    if (isNaN(spec) && this.manifestLighting?.specularIntensity != null) {
      spec = this.manifestLighting.specularIntensity;
    }
    if (!isNaN(spec)) {
      config.specularMultiplier = DEFAULT_LIGHTING.specularMultiplier * spec;
    }

    const specMul = attr('specular-multiplier');
    if (!isNaN(specMul)) {
      config.specularMultiplier = specMul;
    }

    return config;
  }

  private updateLighting(): void {
    if (!this.renderer) return;
    this.renderer.setLighting(this.computeLighting());
  }

  private async initialize(): Promise<void> {
    this.cleanup();
    this.showLoading();
    this.manifestLighting = null; // re-set by loadTextures on the manifest path

    try {
      const textures = await this.loadTextures();
      if (!this._isComponentConnected) return;

      this.createRenderer(textures);
      this.setupInteractivity();
      this.hideLoading();
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Failed to load material');
    }
  }

  private async loadTextures(): Promise<TextureUrls> {
    // Self-describing package: a single manifest.json (AssetData) URL. Texture
    // paths in the manifest are resolved relative to the manifest's location.
    const manifestUrl = this.getAttribute('manifest-url');
    if (manifestUrl) {
      const res = await fetch(manifestUrl);
      if (!res.ok) throw new Error(`Failed to load manifest (${res.status})`);
      const data = (await res.json()) as AssetData;
      this.manifestLighting = data.lighting ?? null;
      const base = new URL(manifestUrl, location.href);
      const abs = (p?: string) => (p ? new URL(p, base).href : undefined);
      return {
        albedo: abs(data.textures.albedo)!,
        albedoPreview: abs(data.textures.albedoPreview),
        normal: abs(data.textures.normal)!,
        normalPreview: abs(data.textures.normalPreview),
        specular: abs(data.textures.specular)!,
        specularPreview: abs(data.textures.specularPreview),
        roughness: abs(data.textures.roughness),
        roughnessPreview: abs(data.textures.roughnessPreview),
        metalness: abs(data.textures.metalness),
        metalnessPreview: abs(data.textures.metalnessPreview),
      };
    }

    // Check for direct URL attributes first
    const albedoUrl = this.getAttribute('albedo-url');
    const normalUrl = this.getAttribute('normal-url');
    const specularUrl = this.getAttribute('specular-url');

    if (albedoUrl && normalUrl && specularUrl) {
      return {
        albedo: albedoUrl,
        albedoPreview: this.getAttribute('albedo-preview-url') || undefined,
        normal: normalUrl,
        normalPreview: this.getAttribute('normal-preview-url') || undefined,
        specular: specularUrl,
        specularPreview: this.getAttribute('specular-preview-url') || undefined,
        roughness: this.getAttribute('roughness-url') || undefined,
        roughnessPreview: this.getAttribute('roughness-preview-url') || undefined,
        metalness: this.getAttribute('metalness-url') || undefined,
        metalnessPreview: this.getAttribute('metalness-preview-url') || undefined,
      };
    }

    // Otherwise, use API
    const assetId = this.getAttribute('asset-id');
    const apiKey = this.getAttribute('api-key');

    if (!assetId || !apiKey) {
      throw new Error('Either provide texture URLs directly or asset-id with api-key');
    }

    const apiUrl = this.getAttribute('api-url') || undefined;
    const client = new ApiClient({ apiKey, baseUrl: apiUrl });
    const textures = await client.getTextures(assetId);

    return {
      albedo: textures.albedo,
      albedoPreview: textures.albedoPreview,
      normal: textures.normal,
      normalPreview: textures.normalPreview,
      specular: textures.specular,
      specularPreview: textures.specularPreview,
      roughness: textures.roughness,
      roughnessPreview: textures.roughnessPreview,
      metalness: textures.metalness,
      metalnessPreview: textures.metalnessPreview,
    };
  }

  private createRenderer(textures: TextureUrls): void {
    if (!this.canvas) return;

    const progressiveLoading = this.getAttribute('progressive-loading') !== 'false';
    const materialScale = parseFloat(this.getAttribute('material-scale') || '1.0');
    const verticalOffset = parseFloat(this.getAttribute('vertical-offset') || '0.0');

    this.renderer = new WebGLRenderer({
      canvas: this.canvas,
      textures,
      lighting: this.computeLighting(),
      materialScale,
      verticalOffset,
      progressiveLoading,
      useSimpleShader: false,
    });
  }

  private setupInteractivity(): void {
    if (!this.canvas || !this.renderer) return;

    const interactive = this.getAttribute('interactive') !== 'false';
    if (!interactive) return;

    // While the pointer is over the material the neutral environment steps
    // back to a subtle fill and the moving key light takes over the lighting.
    this.canvas.addEventListener('mouseenter', () => {
      this.renderer?.setRelight(true);
    });

    // Mouse move on canvas
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas!.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
      this.renderer?.setRelight(true); // enter can be missed (e.g. touch)
      this.renderer?.setLightPosition(x, y);
    });

    // Touch move
    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        const rect = this.canvas!.getBoundingClientRect();
        const touch = e.touches[0];
        const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -(((touch.clientY - rect.top) / rect.height) * 2 - 1);
        this.renderer?.setRelight(true);
        this.renderer?.setLightPosition(x, y);
      }
    }, { passive: false });
    this.canvas.addEventListener('touchend', () => {
      this.renderer?.setRelight(false);
      this.renderer?.setLightPosition(0, 0.3);
    });

    // Reset on mouse leave: environment comes back, light returns to default
    this.canvas.addEventListener('mouseleave', () => {
      this.renderer?.setRelight(false);
      this.renderer?.setLightPosition(0, 0.3);
    });
  }

  private showLoading(): void {
    this.loadingEl?.classList.remove('hidden');
    this.errorEl?.classList.add('hidden');
  }

  private hideLoading(): void {
    this.loadingEl?.classList.add('hidden');
  }

  private showError(message: string): void {
    this.loadingEl?.classList.add('hidden');
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
    }
  }

  private cleanup(): void {
    this.renderer?.destroy();
    this.renderer = null;
    this.resizeObserver?.disconnect();
  }

  // Public API
  public setLightPosition(x: number, y: number): void {
    this.renderer?.setLightPosition(x, y);
  }

  public setLighting(config: Partial<LightingConfig>): void {
    this.renderer?.setLighting(config);
  }

  public refresh(): void {
    this.initialize();
  }
}

// Register custom element
if (typeof customElements !== 'undefined' && !customElements.get('phototron-material')) {
  customElements.define('phototron-material', PhototronMaterial);
}
